import { Loadable } from '@/components/loaders';
import { setRegularPagination } from '@/features/audiences/audiencesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { FrontTable, TablePager } from '@/lib';
import { Audience } from '@/models/audiences';
import { RootState } from '@/reducers';
import { useGetAudiencesCountQuery, useLazyGetAudiencesQuery } from '@/services/endpoints/audiences';
import { Row } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getAudiencesTableColumns } from './helpers';

export const AudiencesTable: FC = () => {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const history = useHistory();

  const [getAudiences, { data, isFetching: isAudiencesFetching, isLoading: isAudiencesLoading }] = useLazyGetAudiencesQuery();
  const [audiences, setAudiences] = useState<Audience[]>([]);

  const searchText = useSelector((state: RootState) => state.audiences.ui.regular.searchText);
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetAudiencesCountQuery({ workspaceId: workspace.id, searchText: validSearchText });

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getAudiences({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      }),
    [searchText]
  );

  const handleRowClick = (row: Row<Audience>) => {
    history.push(`/audiences/${row.original.id}`);
  };

  const columns = useMemo(() => getAudiencesTableColumns(t), [t]);

  return (
    <TablePager
      stateSelector={(state) => state.audiences.ui.regular.pagination}
      stateUpdater={setRegularPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setAudiences}
      querying={isTotalCountFetching || isAudiencesFetching}
      totalCount={totalCount || 0}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <Loadable isLoading={isAudiencesLoading}>
          <FrontTable tableData={audiences} columns={columns} footer={pager} onRowClick={handleRowClick} notFound={t('audiences:table.no_audiences_found')} />
        </Loadable>
      )}
    </TablePager>
  );
};
